import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DashboardWrapper from "../../components/DashboardWrapper";
import CrudTable from "../../components/dashboard/table/CrudTable";
import userManageService from "../../services/userManageService";

const localCacheKey = "customerReadCache";

export default function ReadCustomers() {
  const navigate = useNavigate();
const [searchText, setSearchText] = useState("");
    const columns = [
    "sl", "username", "email", "phone_number", "is_active"
  ];

  const [cachedData, setCachedData] = useState(() => {
    const cache = localStorage.getItem(localCacheKey);
    return cache ? JSON.parse(cache) : null;
  });

  const [localData, setLocalData] = useState(cachedData?.customers || []);
  const [loading, setLoading] = useState(!cachedData);

  const showSwal = useCallback((title, text, icon) => {
    Swal.fire({
      title,
      text,
      icon,
      confirmButtonText: "OK",
      customClass: { confirmButton: "btn btn-primary" },
      buttonsStyling: false,
    });
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await userManageService.getAllCustomers();
      console.log(res);
      const customers = res.data.customers || [];

      const newCache = { customers };
      const existingCache = localStorage.getItem(localCacheKey);
      const parsedCache = existingCache ? JSON.parse(existingCache) : null;

      if (!parsedCache || JSON.stringify(parsedCache.customers) !== JSON.stringify(customers)) {
        localStorage.setItem(localCacheKey, JSON.stringify(newCache));
        setCachedData(newCache);
      }

      setLocalData(customers);
    } catch (error) {
      console.error("Failed to load customers:", error);
      showSwal("Error", "Failed to load customer data.", "error");
    } finally {
      setLoading(false);
    }
  }, [showSwal]);

  useEffect(() => {
    if (cachedData) {
      setLocalData(cachedData.customers);
      setLoading(false);
      loadData(); // background refresh
    } else {
      loadData();
    }
  }, [cachedData, loadData]);

  const updateLocalDataCache = (updatedCustomers) => {
    const newCache = { customers: updatedCustomers };
    setLocalData(updatedCustomers);
    setCachedData(newCache);
    localStorage.setItem(localCacheKey, JSON.stringify(newCache));
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      customClass: {
        confirmButton: "btn btn-primary m-1",
        cancelButton: "btn btn-danger m-1",
      },
      buttonsStyling: false,
    });

    if (confirm.isConfirmed) {
      try {
        await userManageService.deleteCustomer(id);
        const updated = localData.filter((item) => item.id !== id);
        updateLocalDataCache(updated);
        showSwal("Deleted!", "Customer has been deleted.", "success");
      } catch (error) {
        console.error("Delete error:", error);
        showSwal("Error", "Failed to delete customer.", "error");
      }
    }
  };

  const handleToggleStatus = useCallback(
    async (item) => {
      try {
        await userManageService.toggleCustomerStatus(item.id);
        const updated = localData.map((customer) =>
          customer.id === item.id ? { ...customer, is_active: !customer.is_active } : customer
        );
        updateLocalDataCache(updated);
        showSwal("Success!", "Customer status updated.", "success");
      } catch (error) {
        console.error("Status update failed:", error);
        showSwal("Error!", "Failed to update customer status.", "error");
      }
    },
    [localData, showSwal]
  );

  return (
    <DashboardWrapper>
     <div className="container-fluid mt-4">
         <div className="d-flex justify-content-between align-items-center mb-4">
          <button
            className="btn btn-secondary d-flex align-items-center justify-content-between"
            onClick={() => navigate(-1)}
          >
            <span>Back</span>
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => navigate("/admin/customer/create")} 
             style={{ backgroundColor: "#214b7a", borderColor: "#214b7a", color: "#fff" }}
          >
            Create Customer
          </button>
        </div>

        <div className="card shadow">
                           <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-3">
       <h4 className="mb-0">Customer Management</h4>

  <input
    type="text"
    className="form-control"
    placeholder="Search ..."
    value={searchText}
    onChange={(e) => setSearchText(e.target.value)}
    style={{
      width: "260px",
      maxWidth: "100%",
      borderRadius: "6px",
    }}
  />
</div>

          <div className="card-body">

          {loading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: "200px" }}
            >
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <CrudTable
               data={localData.map((item, index) => ({
                  ...item,
                  sl: index + 1,
                }))}
              onEdit={(row) => navigate(`/admin/customer/edit/${row.id}`)}
              onDelete={handleDelete}
              onToggleStatus={handleToggleStatus}
               searchText={searchText}
              columns={columns}
            />
          )}
        </div>
        </div>
      </div>
    </DashboardWrapper>
  );
}
